import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useEffect, useState } from 'react';
import sha1 from 'crypto-js/sha1';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { FormContainer, FormErrorProvider, useForm } from "react-hook-form-mui";
import { BoxContainer } from '../layouts/Theme';
import { useI18N } from '../context/I18NContext';
import { useSettings } from '../context/SettingsContext';
import { useFlow } from "../context/FlowContext";
import useWidgetStore from '../store/useWidgetStore';
import { useSubscriptions } from '../hooks/query/useSubscriptions';
import { useAbort } from '../hooks/query/useAbort';
import { useVoucher } from '../hooks/query/useVouchers';
import { usePrepareCart } from '../hooks/query/usePrepareCart';
import { Item } from '../../../layouts/Theme';
import ConsumerSummary from '../partials/checkout/ConsumerSummary';
import RegisterForm from '../partials/account/RegisterForm';
import CartSummary from '../partials/checkout/CartSummary';
import CartSimple from '../partials/checkout/CartSimple';
import ErrorAlert from '../partials/ErrorAlert';
export default function Summary(props) {
    const flow = useFlow();
    const { orientation, widgetMode, hasShop } = useSettings();
    const { booking, enabled, setEnabled, setBooking, cart, setCart, consumer, setConsumer, setPaymentMethods, setPaymentOptions, setFees } = useWidgetStore();
    const { t } = useI18N();
    const formContextPrepareCart = useForm({});
    const prepareCartQuery = usePrepareCart(formContextPrepareCart);
    const abortQuery = useAbort();
    const voucherQuery = useVoucher();
    const subscriptionsQuery = useSubscriptions();
    const [errors, setErrors] = useState();
    const [hasExtraProducts, setHasExtraProducts] = useState(false);
    const [hasAppointmentProducts, setHasAppointmentProducts] = useState(false);
    // Whether getSubscriptionTypes has answered at least once, so consumer.subscriptions holds
    // what this appointment can be paid with. Not read off the query: it is keyed on the consumer
    // and the booking with cacheTime 0, so every write to either moves the observer to a fresh
    // query whose `data` is undefined again — and because the queryFn consumes react-query's
    // `signal`, the request that was in flight for the old key is aborted outright. Submitting the
    // summary does exactly that (setCustomer writes a new consumer.lastUpdate), so the answer this
    // view exists to collect is thrown away right when it is needed.
    const [subscriptionsAnswered, setSubscriptionsAnswered] = useState(false);
    // prepareCart was asked for while the gate below was still shut; fire it as soon as it opens
    const [prepareCartQueued, setPrepareCartQueued] = useState(false);
    // ... and never let a lookup that does not come back block the booking altogether
    const [subscriptionsTimedOut, setSubscriptionsTimedOut] = useState(false);
    // prepareCart's answer decides the payment methods and navigates straight on to the payment
    // view, which unmounts this one — the only place getSubscriptionTypes is asked. So it must not
    // go out until we have that answer, or the payment view offers nothing but "losse afspraak".
    // An abort in flight is the other reason to wait: the order it cancels still exists server
    // side, so a prepareCart that races it comes back about the time slot.
    const canPrepareCart = () => (subscriptionsAnswered || subscriptionsTimedOut)
        && !abortQuery.isFetching
        && !abortQuery.isRefetching;
    // the submit button stays disabled while we hold prepareCart back, so the wait reads as the
    // step it is instead of a dead button
    const disableSubmit = abortQuery.isRefetching || abortQuery.isFetching || prepareCartQueued;
    useEffect(() => {
        var _a;
        setHasExtraProducts(cart.lines.filter((line) => line.origin !== 'appointment').length > 0);
        setHasAppointmentProducts(cart.lines.filter((line) => line.origin === 'appointment').length > 0 || ((_a = booking.appointments) === null || _a === void 0 ? void 0 : _a.length) > 0);
    }, [cart.lines]);
    useEffect(() => {
        if (abortQuery.data) {
            let productLines = cart.lines.filter((line) => line.origin !== 'appointment');
            setCart(Object.assign(Object.assign({}, cart), { order_id: undefined, lines: productLines }));
            subscriptionsQuery.refetch();
        }
    }, [abortQuery.data]);
    useEffect(() => {
        if (cart && (cart === null || cart === void 0 ? void 0 : cart.order_id)) {
            abortQuery.refetch();
        }
        setEnabled(Object.assign(Object.assign({}, enabled), { apptype: true, resource: true, product: true }));
    }, []);
    useEffect(() => {
        voucherQuery.refetch();
    }, [booking.appointments]);
    useEffect(() => {
        if (subscriptionsQuery.data) {
            let subscriptions = subscriptionsQuery.data.result.items;
            subscriptions = subscriptions.filter((subscription) => {
                return subscription.subscriptionId || (subscription.buyable || subscription.mandatable);
            });
            setConsumer(Object.assign(Object.assign({}, consumer), { subscriptions: subscriptions }));
            setSubscriptionsAnswered(true);
        }
    }, [subscriptionsQuery.data]);
    useEffect(() => {
        // a lookup that fails tells us nothing, but holding the booking hostage to it would be
        // worse than the answer being empty
        if (subscriptionsQuery.isError) {
            setSubscriptionsAnswered(true);
        }
    }, [subscriptionsQuery.isError]);
    useEffect(() => {
        if (!prepareCartQueued) {
            return;
        }
        if (canPrepareCart()) {
            setPrepareCartQueued(false);
            prepareCartQuery.refetch();
            return;
        }
        // getSubscriptionTypes is a good deal slower on some accounts than prepareCart is (seconds,
        // not milliseconds), which is the whole reason this gate exists — but a backend that never
        // answers must not leave the consumer on a summary that does nothing.
        const timer = setTimeout(() => setSubscriptionsTimedOut(true), 15000);
        return () => clearTimeout(timer);
    }, [prepareCartQueued, subscriptionsAnswered, subscriptionsTimedOut, abortQuery.isFetching, abortQuery.isRefetching]);
    useEffect(() => {
        if (voucherQuery.data) {
            let _discounts = voucherQuery.data.result.items.filter((voucher) => voucher.type !== "giftCard");
            let _giftCards = voucherQuery.data.result.items.filter((voucher) => voucher.type === "giftCard");
            setCart(Object.assign(Object.assign({}, cart), { discounts: _discounts, giftCards: _giftCards }));
        }
    }, [voucherQuery.data]);
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f, _g;
        if (prepareCartQuery.data) {
            if (prepareCartQuery.data.status.status === 'failed') {
                if (prepareCartQuery.data.status.userMessages) {
                    if (prepareCartQuery.data.status.userMessages[0].startsWith("Deze afspraak kan alleen")) {
                        // if (prepareCartQuery.data.status.userMessages.includes("Deze afspraak kan alleen met een strippenkaart of abonnement betaald worden.")) {
                        if (consumer.subscriptions && consumer.subscriptions.length > 0) {
                            // set a "random" but unique order_id so we can get to the payment step
                            setCart(Object.assign(Object.assign({}, cart), { order_id: 'tmp_'.concat(sha1(JSON.stringify(consumer.subscriptions)).toString()) }));
                            // we only have alternate payment methods
                            setPaymentMethods(["alternate"]);
                            // if (flow.onNext && cart.order_id) {
                            if (flow.onNext) {
                                // these never change, and by setting them here we can render the next view instantly
                                flow.onNext();
                            }
                        }
                        else {
                            // we should never get here, as this apptype can only be paid with a membership or subscription,
                            // but there are no memberships/subscriptions to use or buy
                            let _errors = [];
                            _errors.push(prepareCartQuery.data.status.message);
                            Object.values(prepareCartQuery.data.status.userMessages).map((error, i) => {
                                _errors.push(error);
                            });
                            setErrors(_errors);
                        }
                    }
                    else {
                        let _errors = [];
                        _errors.push(prepareCartQuery.data.status.message);
                        Object.values(prepareCartQuery.data.status.userMessages).map((error, i) => {
                            _errors.push(error);
                        });
                        setErrors(_errors);
                    }
                }
                else {
                    setErrors([prepareCartQuery.data.status.message]);
                }
            }
            else {
                if (prepareCartQuery.data.result.item.paymentRequired && ((_a = prepareCartQuery.data.result.item.order) === null || _a === void 0 ? void 0 : _a.order_id)) {
                    setCart(Object.assign(Object.assign({}, cart), { order_id: (_b = prepareCartQuery.data.result.item.order) === null || _b === void 0 ? void 0 : _b.order_id, token: prepareCartQuery.data.result.item.csrfToken }));
                    let methods = prepareCartQuery.data.result.item.paymentMethods, 
                    // 'none' and 'alternate' are not external providers: 'none' is paying at the
                    // counter and 'alternate' is a subscription or strippenkaart. Counting either
                    // as an external adds a "Losse afspraak maken" option the backend never
                    // offered, and hides the "Niet online afrekenen" one that it did.
                    externals = (_c = methods === null || methods === void 0 ? void 0 : methods.filter((method) => method !== 'none' && method !== 'alternate')) !== null && _c !== void 0 ? _c : [];
                    if (externals.length > 0) {
                        methods.push("external");
                    }
                    if (consumer.subscriptions && consumer.subscriptions.length > 0 && hasAppointmentProducts) {
                        // if subscription has a subscriptionId, we own and can use it — the backend
                        // returns 'alternate' itself these days, so only add it if it is missing
                        if (!methods.includes("alternate")) {
                            methods.push("alternate");
                        }
                    }
                    setPaymentMethods(methods);
                    let _paymentOptions = prepareCartQuery.data.result.item.paymentOptions;
                    setPaymentOptions(_paymentOptions);
                    // @ts-ignore
                    setFees(prepareCartQuery.data.result.item.fees);
                    if (flow.onNext && cart.order_id) {
                        // these never change, and by setting them here we can render the next view instantly
                        flow.onNext();
                    }
                }
                else {
                    setCart(Object.assign(Object.assign({}, cart), { order_id: undefined }));
                    if (flow.onNext) {
                        if (((_d = flow.nextRoute) === null || _d === void 0 ? void 0 : _d.route) === 'thankYou') {
                            let thankYou = flow.views.find((view) => {
                                return view.route.route === 'thankYou';
                            });
                            if ((_e = thankYou === null || thankYou === void 0 ? void 0 : thankYou.config) === null || _e === void 0 ? void 0 : _e.redirect) {
                                if (widgetMode === 'standalone') {
                                    window.location.href = (_f = thankYou === null || thankYou === void 0 ? void 0 : thankYou.config) === null || _f === void 0 ? void 0 : _f.redirect;
                                }
                                else {
                                    window.parent.postMessage({
                                        type: 'redirect',
                                        url: (_g = thankYou === null || thankYou === void 0 ? void 0 : thankYou.config) === null || _g === void 0 ? void 0 : _g.redirect
                                    }, '*');
                                }
                                // const inFrame = typeof window !== 'undefined' && window.self !== window.top,
                                //     parent = window.top
                                // if (inFrame && parent) {
                                //     parent.location.href = thankYou?.config?.redirect
                                //     return
                                // }
                                // else {
                                //     window.location.href = thankYou?.config?.redirect
                                //     return
                                // }
                            }
                        }
                        flow.onNext();
                    }
                }
            }
        }
    }, [prepareCartQuery.data]);
    useEffect(() => {
        // If we have a pending transaction, go to the next view which is now Payment
        // if (cart && cart?.transactions[0]?.id && cart?.transactions[0]?.status === "pending") {
        if (cart && (cart === null || cart === void 0 ? void 0 : cart.order_id)) {
            if (flow.onNext) {
                flow.onNext();
            }
        }
    }, [cart === null || cart === void 0 ? void 0 : cart.order_id]);
    const registerFormCallback = (data) => {
        setBooking(Object.assign(Object.assign({}, booking), { remark: data.remark }));
        if (canPrepareCart()) {
            prepareCartQuery.refetch();
        }
        else {
            setPrepareCartQueued(true);
        }
    };
    const onError = (errors) => { };
    return (_jsx(_Fragment, { children: _jsxs(BoxContainer, { sx: {
                maxWidth: orientation === "horizontal"
                    ? window.innerWidth >= 1350 ? '100%' : '700px'
                    : "468px", // 500px - 2x 16px padding
                paddingTop: 2
            }, orientation: orientation, children: [(prepareCartQuery.isRefetching || prepareCartQueued) && (_jsx(_Fragment, { children: _jsxs(Box, { sx: { mt: '50vh', width: '100%' }, children: [_jsx(Typography, { component: "h1", variant: "h6", style: { margin: '2vh', textAlign: 'center' }, children: t('loading') }), _jsx(LinearProgress, {})] }) })), !(prepareCartQuery.isRefetching || prepareCartQueued) && (_jsxs(_Fragment, { children: [orientation === "horizontal" && window.innerWidth >= 1350 && ( // 500px per column plus all widget padding and margins
                        _jsx(_Fragment, { children: _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { item: true, xs: 6, md: 6, children: _jsxs(Stack, { spacing: 2, children: [_jsxs(Item, { children: [hasShop && (_jsx(CartSimple, {})), !hasShop && (_jsx(CartSummary, {}))] }), _jsx(Item, { children: consumer && (_jsx(ConsumerSummary, {})) })] }) }), _jsx(Grid, { item: true, xs: 6, md: 6, children: _jsxs(Stack, { spacing: 2, children: [_jsx(Item, { children: _jsx(RegisterForm, { mode: "summary", registerRequired: false, disableSubmit: disableSubmit, callback: registerFormCallback }) }), _jsx(Item, { children: _jsx(FormContainer, { formContext: formContextPrepareCart, onError: onError, children: _jsx(FormErrorProvider, { onError: (error) => {
                                                                if (error.type === 'validate') {
                                                                    console.log(error);
                                                                }
                                                                return t(error.type, 'error');
                                                            }, children: _jsx(ErrorAlert, { messages: errors, setter: setErrors, noAutoClose: true }) }) }) })] }) })] }) })), (orientation === "vertical" || window.innerWidth < 1350) && (_jsxs(_Fragment, { children: [_jsxs(Box, { children: [hasShop && (_jsx(CartSimple, {})), !hasShop && (_jsx(CartSummary, {})), _jsx(ConsumerSummary, {}), _jsx(Divider, { sx: { my: 4 } })] }), _jsx(RegisterForm, { mode: "summary", registerRequired: false, disableSubmit: disableSubmit, callback: registerFormCallback }), _jsx(FormContainer, { formContext: formContextPrepareCart, onError: onError, children: _jsx(FormErrorProvider, { onError: (error) => {
                                            if (error.type === 'validate') {
                                                console.log(error);
                                            }
                                            return t(error.type, 'error');
                                        }, children: _jsx(ErrorAlert, { messages: errors, setter: setErrors, noAutoClose: true }) }) })] }))] }))] }) }));
}
